import { useMemo } from 'react'
import Box from '@mui/material/Box'
import type { AudienceFilters } from '@brushfire/types'
import type { DashboardStatusFilterOption } from '@/features/dashboard/components/DashboardStatusFilter'
import {
  CHILDREN_OPTIONS,
  CITIES_BY_STATE,
  EDUCATION_OPTIONS,
  EMPLOYMENT_OPTIONS,
  GENDER_OPTIONS,
  HOUSING_OPTIONS,
  HOUSEHOLD_OPTIONS,
  INDUSTRY_OPTIONS,
  MARITAL_OPTIONS,
  US_STATES,
} from '@/features/instruments/publish/target-audience/lib/demographicFilterCatalog'
import {
  SP_LIST_DEMOGRAPHIC_FILTER_COL_WIDTH_PX,
  SP_LIST_DEMOGRAPHIC_FILTER_ROW_GAP_PX,
  SP_LIST_DEMOGRAPHIC_PANEL_PADDING_X_PX,
} from '../lib/signal-provider-filter-layout'
import { SignalProviderDemographicFilterField } from './SignalProviderDemographicFilterField'

const ANY_OPTION = { value: 'all', label: 'Any' } as const

const AGE_PRESET_OPTIONS: readonly DashboardStatusFilterOption<string>[] = [
  ANY_OPTION,
  { value: '18-24', label: '18–24' },
  { value: '25-34', label: '25–34' },
  { value: '35-44', label: '35–44' },
  { value: '45-54', label: '45–54' },
  { value: '55-64', label: '55–64' },
  { value: '65+', label: '65+' },
]

const DEVICE_OPTIONS: readonly DashboardStatusFilterOption<string>[] = [
  ANY_OPTION,
  { value: 'mobile', label: 'Mobile' },
  { value: 'web', label: 'Web' },
  { value: 'mobile,web', label: 'Mobile & Web' },
]

function agePresetFromRange(range?: AudienceFilters['ageRange']): string {
  if (!range?.min && !range?.max) return 'all'
  if (range.min === 18 && range.max === 24) return '18-24'
  if (range.min === 25 && range.max === 34) return '25-34'
  if (range.min === 35 && range.max === 44) return '35-44'
  if (range.min === 45 && range.max === 54) return '45-54'
  if (range.min === 55 && range.max === 64) return '55-64'
  if (range.min === 65 && range.max == null) return '65+'
  return 'all'
}

function ageRangeFromPreset(preset: string): AudienceFilters['ageRange'] | undefined {
  switch (preset) {
    case '18-24':
      return { min: 18, max: 24 }
    case '25-34':
      return { min: 25, max: 34 }
    case '35-44':
      return { min: 35, max: 44 }
    case '45-54':
      return { min: 45, max: 54 }
    case '55-64':
      return { min: 55, max: 64 }
    case '65+':
      return { min: 65 }
    default:
      return undefined
  }
}

function withAnyOption(options: { value: string; label: string }[]): DashboardStatusFilterOption<string>[] {
  return [ANY_OPTION, ...options]
}

function setSingleArrayFilter(
  filters: AudienceFilters,
  key: 'gender' | 'education' | 'employment' | 'maritalStatus' | 'householdSize' | 'numberOfChildren' | 'industry' | 'housingStatus',
  value: string,
): AudienceFilters {
  const next = { ...filters }
  if (value === 'all') delete next[key]
  else next[key] = [value]
  return next
}

interface Props {
  filters: AudienceFilters
  onChange: (next: AudienceFilters) => void
}

/** Figma `37:792` — 12 demographic dropdown filters in a 2-column popover panel. */
export function SignalProviderDemographicFiltersPanel({ filters, onChange }: Props) {
  const stateValue = filters.location?.state ?? 'all'
  const cityOptions = useMemo(
    () => (stateValue !== 'all' ? CITIES_BY_STATE[stateValue] ?? [] : []),
    [stateValue],
  )
  const cityValue = filters.location?.city ?? 'all'
  const deviceValue =
    filters.deviceType?.length === 2
      ? 'mobile,web'
      : filters.deviceType?.[0] ?? 'all'

  const updateLocation = (partial: { state?: string; city?: string }) => {
    const next = { ...filters }
    const state = partial.state ?? filters.location?.state
    const city = partial.city ?? filters.location?.city
    if (!state && !city) {
      delete next.location
    } else {
      next.location = {}
      if (state) next.location.state = state
      if (city) next.location.city = city
    }
    if (partial.state && partial.state !== filters.location?.state) {
      delete next.location?.city
    }
    onChange(next)
  }

  const dropdowns: {
    key: string
    label: string
    value: string
    options: readonly DashboardStatusFilterOption<string>[]
    onChange: (value: string) => void
    testId: string
  }[] = [
    {
      key: 'age',
      label: 'Age range',
      value: agePresetFromRange(filters.ageRange),
      options: AGE_PRESET_OPTIONS,
      onChange: (value) => {
        const next = { ...filters }
        const range = ageRangeFromPreset(value)
        if (range) next.ageRange = range
        else delete next.ageRange
        onChange(next)
      },
      testId: 'sp-filter-age-range',
    },
    {
      key: 'state',
      label: 'State',
      value: stateValue,
      options: withAnyOption(US_STATES),
      onChange: (value) => {
        if (value === 'all') updateLocation({ state: undefined, city: undefined })
        else updateLocation({ state: value, city: undefined })
      },
      testId: 'sp-filter-state',
    },
    {
      key: 'city',
      label: 'City',
      value: cityValue,
      options: withAnyOption(cityOptions),
      onChange: (value) => {
        if (value === 'all') updateLocation({ city: undefined })
        else updateLocation({ city: value })
      },
      testId: 'sp-filter-city',
    },
    {
      key: 'employment',
      label: 'Employment Status',
      value: filters.employment?.[0] ?? 'all',
      options: withAnyOption(EMPLOYMENT_OPTIONS),
      onChange: (value) => onChange(setSingleArrayFilter(filters, 'employment', value)),
      testId: 'sp-filter-employment',
    },
    {
      key: 'household',
      label: 'Household size',
      value: filters.householdSize?.[0] ?? 'all',
      options: withAnyOption(HOUSEHOLD_OPTIONS),
      onChange: (value) => onChange(setSingleArrayFilter(filters, 'householdSize', value)),
      testId: 'sp-filter-household-size',
    },
    {
      key: 'industry',
      label: 'Industry/Field',
      value: filters.industry?.[0] ?? 'all',
      options: withAnyOption(INDUSTRY_OPTIONS),
      onChange: (value) => onChange(setSingleArrayFilter(filters, 'industry', value)),
      testId: 'sp-filter-industry',
    },
    {
      key: 'device',
      label: 'Device type',
      value: deviceValue,
      options: DEVICE_OPTIONS,
      onChange: (value) => {
        const next = { ...filters }
        if (value === 'all') delete next.deviceType
        else if (value === 'mobile,web') next.deviceType = ['mobile', 'web']
        else next.deviceType = [value as 'mobile' | 'web']
        onChange(next)
      },
      testId: 'sp-filter-device-type',
    },
    {
      key: 'gender',
      label: 'Gender',
      value: filters.gender?.[0] ?? 'all',
      options: withAnyOption(GENDER_OPTIONS),
      onChange: (value) => onChange(setSingleArrayFilter(filters, 'gender', value)),
      testId: 'sp-filter-gender',
    },
    {
      key: 'education',
      label: 'Education level',
      value: filters.education?.[0] ?? 'all',
      options: withAnyOption(EDUCATION_OPTIONS),
      onChange: (value) => onChange(setSingleArrayFilter(filters, 'education', value)),
      testId: 'sp-filter-education',
    },
    {
      key: 'marital',
      label: 'Marital Status',
      value: filters.maritalStatus?.[0] ?? 'all',
      options: withAnyOption(MARITAL_OPTIONS),
      onChange: (value) => onChange(setSingleArrayFilter(filters, 'maritalStatus', value)),
      testId: 'sp-filter-marital-status',
    },
    {
      key: 'children',
      label: 'Number of children',
      value: filters.numberOfChildren?.[0] ?? 'all',
      options: withAnyOption(CHILDREN_OPTIONS),
      onChange: (value) => onChange(setSingleArrayFilter(filters, 'numberOfChildren', value)),
      testId: 'sp-filter-children',
    },
    {
      key: 'housing',
      label: 'Housing Status',
      value: filters.housingStatus?.[0] ?? 'all',
      options: withAnyOption(HOUSING_OPTIONS),
      onChange: (value) => onChange(setSingleArrayFilter(filters, 'housingStatus', value)),
      testId: 'sp-filter-housing-status',
    },
  ]

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: `repeat(2, minmax(0, ${SP_LIST_DEMOGRAPHIC_FILTER_COL_WIDTH_PX}px))`,
        },
        columnGap: `${SP_LIST_DEMOGRAPHIC_PANEL_PADDING_X_PX}px`,
        rowGap: `${SP_LIST_DEMOGRAPHIC_FILTER_ROW_GAP_PX}px`,
        px: `${SP_LIST_DEMOGRAPHIC_PANEL_PADDING_X_PX}px`,
        width: { xs: 280, sm: 428 },
      }}
      data-testid="signal-provider-demographic-filters-panel"
    >
      {dropdowns.map((dropdown) => (
        <SignalProviderDemographicFilterField
          key={dropdown.key}
          label={dropdown.label}
          value={dropdown.value}
          onChange={dropdown.onChange}
          options={dropdown.options}
          testId={dropdown.testId}
        />
      ))}
    </Box>
  )
}
